import type { HttpQueryParam, IHttpClient } from "@/core/interfaces";
import { extractTotal, toPaginatedQuery } from "@/core/utils";
import { Registry } from "@/shared/infra/dependency-injection/registry";

import {
  cabecalhoIdempotencia,
  readEnvelopeData,
} from "../financeiro-obras/financeiro-obras.gateway";
import type {
  LookupRequestOptions,
  OpcoesMutacao,
} from "../financeiro-obras/financeiro-obras.gateway";
import type {
  EditarEscopoPadraoBody,
  EquipamentoOpcao,
  EscopoPadraoGravado,
  ListaEscoposPadrao,
  ListarEquipamentosParams,
  ListarEscoposPadraoParams,
  NovaVersaoBody,
  NovoEscopoPadraoBody,
  ProximaVersao,
  SituacaoEscopoPadrao,
  VersaoCriada,
  VersaoEscopoPadrao,
} from "./financeiro-configuracoes.escopos-padrao.types";

const BASE = "/api/financeiro/configuracoes/escopos-macro-padrao";

export const ESCOPOS_PADRAO_PATHS = {
  ESCOPOS: BASE,
  ESCOPO: (escopoId: string) => `${BASE}/${encodeURIComponent(escopoId)}`,
  SITUACAO: (escopoId: string) => `${BASE}/${encodeURIComponent(escopoId)}/situacao`,
  VERSOES: `${BASE}/versoes`,
  PROXIMA_VERSAO: `${BASE}/versoes/proxima`,
  EQUIPAMENTOS: `${BASE}/equipamentos`,
} as const;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

export async function getVersoesEscopoPadrao({ signal }: LookupRequestOptions = {}): Promise<
  VersaoEscopoPadrao[]
> {
  const response = await getHttpClient().get<unknown>(ESCOPOS_PADRAO_PATHS.VERSOES, { signal });
  const versoes = readEnvelopeData<VersaoEscopoPadrao[]>(response.data);
  if (!Array.isArray(versoes)) throw new Error("Resposta inválida do bff.");
  return versoes;
}

export async function getEscoposPadrao(
  {
    versao,
    visao,
    tipoObraId,
    grupoEscopoMacroId,
    embarcacaoId,
    incluirInativos,
    page,
    pageSize,
    sort,
  }: ListarEscoposPadraoParams,
  { signal }: LookupRequestOptions = {},
): Promise<ListaEscoposPadrao> {
  const params: Record<string, HttpQueryParam> = {
    ...toPaginatedQuery({ page, pageSize, sort }),
    versao,
    visao,
  };
  if (tipoObraId) params.tipoObraId = tipoObraId;
  if (grupoEscopoMacroId) params.grupoEscopoMacroId = grupoEscopoMacroId;
  if (embarcacaoId) params.embarcacaoId = embarcacaoId;
  if (incluirInativos !== undefined) params.incluirInativos = incluirInativos;

  const response = await getHttpClient().get<unknown>(ESCOPOS_PADRAO_PATHS.ESCOPOS, {
    params,
    signal,
  });
  const { items, versao: consultada } = readEnvelopeData<Partial<ListaEscoposPadrao>>(
    response.data,
  );
  if (!Array.isArray(items)) throw new Error("Resposta inválida do bff.");

  return { versao: consultada ?? null, items, total: extractTotal(response.data) ?? items.length };
}

export async function getEquipamentosEscopoPadrao(
  { embarcacaoId, grupoEscopoMacroId }: ListarEquipamentosParams,
  { signal }: LookupRequestOptions = {},
): Promise<EquipamentoOpcao[]> {
  const response = await getHttpClient().get<unknown>(ESCOPOS_PADRAO_PATHS.EQUIPAMENTOS, {
    params: { embarcacaoId, grupoEscopoMacroId },
    signal,
  });
  const equipamentos = readEnvelopeData<EquipamentoOpcao[]>(response.data);
  if (!Array.isArray(equipamentos)) throw new Error("Resposta inválida do bff.");
  return equipamentos;
}

export async function criarEscopoPadrao(
  body: NovoEscopoPadraoBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<EscopoPadraoGravado> {
  const response = await getHttpClient().post<unknown>(ESCOPOS_PADRAO_PATHS.ESCOPOS, body, {
    headers: cabecalhoIdempotencia(idempotencyKey),
  });
  return readEnvelopeData<EscopoPadraoGravado>(response.data);
}

export async function editarEscopoPadrao(
  escopoId: string,
  body: EditarEscopoPadraoBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<EscopoPadraoGravado> {
  const response = await getHttpClient().put<unknown>(ESCOPOS_PADRAO_PATHS.ESCOPO(escopoId), body, {
    headers: cabecalhoIdempotencia(idempotencyKey),
  });
  return readEnvelopeData<EscopoPadraoGravado>(response.data);
}

export async function alterarSituacaoEscopoPadrao(
  escopoId: string,
  situacao: SituacaoEscopoPadrao,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<{ id: string; situacao: SituacaoEscopoPadrao }> {
  const response = await getHttpClient().patch<unknown>(
    ESCOPOS_PADRAO_PATHS.SITUACAO(escopoId),
    { situacao },
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<{ id: string; situacao: SituacaoEscopoPadrao }>(response.data);
}

export async function getProximaVersao({
  signal,
}: LookupRequestOptions = {}): Promise<ProximaVersao> {
  const response = await getHttpClient().get<unknown>(ESCOPOS_PADRAO_PATHS.PROXIMA_VERSAO, {
    signal,
  });
  return readEnvelopeData<ProximaVersao>(response.data);
}

export async function criarNovaVersao(
  body: NovaVersaoBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<VersaoCriada> {
  const response = await getHttpClient().post<unknown>(ESCOPOS_PADRAO_PATHS.VERSOES, body, {
    headers: cabecalhoIdempotencia(idempotencyKey),
  });
  return readEnvelopeData<VersaoCriada>(response.data);
}
